import { LoadingSpinner } from '@app/components/common/LoadingSpinner';
import { ApiKey } from '@app/api/api-keys/types';

import { formatDate } from '../utils/dates';
import { DeleteButton } from './DeleteButton';

interface KeysListProps {
  keys: ApiKey[];
  keys: ApiKey[];
  isLoading: boolean;
  onDelete: (id: string) => void;
  onCancelDelete: (id: string) => void;
  pendingDeletions: Set<string>;
  deletingKeys?: Set<string>;
  className?: string;
}

export function KeysList({
  keys,
  isLoading,
  onDelete,
  onCancelDelete,
  pendingDeletions,
  deletingKeys = new Set(),
  className = '',
}: KeysListProps) {
  return (
    <div className={`space-y-3 -mx-2 sm:mx-0 ${className}`}>
      {isLoading ? (
        <div className="py-16 flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : keys.length === 0 ? (
        <div className="py-16 text-center text-gray-500">No API keys to display</div>
      ) : (
        keys.map((key) => (
          <div
            key={key.id}
            className="border rounded-lg hover:shadow-sm transition-shadow dark:border-gray-700"
          >
            <div className="p-3">
              {/* Header with name and status */}
              <div className="flex items-center justify-between mb-2">
                <div className="font-medium text-gray-900 dark:text-gray-100">{key.name}</div>
                <span
                  className={`shrink-0 px-2 py-0.5 text-xs font-semibold rounded-full ${
                    isKeyExpired(key.expires_at)
                      ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                      : key.is_active
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                  }`}
                >
                  {isKeyExpired(key.expires_at) ? 'Expired' : key.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>

              {/* Key ID */}
              <div className="font-mono text-sm text-gray-600 dark:text-gray-400 break-all mb-2">
                {key.id}
              </div>

              {/* Bottom section with dates and delete button */}
              <div className="flex items-end justify-between text-xs text-gray-500 dark:text-gray-400">
                <div className="grid gap-1">
                  <div>Created: {formatDate(key.created_at)}</div>
                  <div>Expires: {key.expires_at ? formatDate(key.expires_at) : 'Never'}</div>
                </div>
                <div className="shrink-0 ml-4">
                  <DeleteButton
                    onDelete={() => onDelete(key.id)}
                    onCancel={() => onCancelDelete(key.id)}
                    isPending={pendingDeletions.has(key.id)}
                    isDeleting={deletingKeys.has(key.id)}
                  />
                </div>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function isKeyExpired(expiresAt: Date | null | undefined): boolean {
  if (!expiresAt) return false;
  return new Date(expiresAt).getTime() < new Date().getTime();
}
